import { MoveToProjectModal } from '@/components/MoveToProjectModal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useParsedMessages } from '@/pages/Chat/components/Chat/useMessageObject';
import { useMessagesService, useUpdateChatService } from '@/services/chat';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FolderPlus, MoreHorizontal, Trash2 } from 'lucide-react';
import { paths } from '@/paths';
import { FC, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

export const ChatCard: FC<{
  id: string;
  title: string;
  createdAt: Date;
}> = ({ id, title, createdAt }) => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const { updateChat, isUpdatingChat } = useUpdateChatService();

  dayjs.extend(relativeTime);

  const formattedDate = dayjs(createdAt).fromNow();

  const { messages } = useMessagesService(id);

  const parsedAssistantMessages = useParsedMessages(messages);
  const lastMessage = messages[messages.length - 1];

  const handleMoveToProject = (projectId: string) => {
    updateChat(
      { chatId: id, data: { projectId } },
      { onSuccess: () => toast.success('Chat added to project') }
    );
  };

  return (
    <>
      <Link
        to={`/workspace/${workspaceUname}/${paths.chats}/${id}`}
        className="w-full"
      >
        <FlexRow
          alignItems="start"
          px="1"
          py="4"
          w="full"
          elevation="b-200"
          className="border-b border-tertiary group"
          gap="2"
        >
          <FlexRow alignItems="center" justifyContent="between" w="full">
            <FlexCol gap="2" className="max-w-[80%]">
              <Text
                variant="body-sm-bold"
                color="primary"
                className="group-hover:underline"
              >
                {title}
              </Text>
              <Ellipsis asChild lineClamp={2}>
                <Text variant="body-sm" color="secondary">
                  {lastMessage?.role === 'assistant'
                    ? parsedAssistantMessages[
                        parsedAssistantMessages.length - 1
                      ]?.content
                    : lastMessage?.parts
                        .map(part => (part.type === 'text' ? part.text : ''))
                        .join('')}
                </Text>
              </Ellipsis>

              <Text variant="body-sm" color="tertiary">
                {formattedDate}
              </Text>
            </FlexCol>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <DropdownMenuItem onSelect={() => setIsMoveOpen(true)}>
                  <FolderPlus className="h-4 w-4" />
                  Add to project
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setIsDeleteOpen(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </FlexRow>
        </FlexRow>
      </Link>
      <DeleteConfirmationModal
        id={id}
        title={title}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
      />
      <MoveToProjectModal
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
        onSelect={handleMoveToProject}
        isLoading={isUpdatingChat}
      />
    </>
  );
};
